/**
 * Indexable SEO URLs: small whitelist Google should treat as primary landing pages.
 * Other marketing URLs may still render long-form articles via `SEO_ARTICLE_PAGE_KEYS`
 * but receive `noindex, follow` in App.jsx.
 */
import { toLogicalPathname, rewriteLegacyMarketingLogicalPath } from './localeSeoRoutes'

export const SEO_LANDING_PATHS = new Set([
  '/docusign-alternative',
  '/how-to-sign-documents-online',
  '/free-contract-templates',
  '/esignature-for-freelancers',
  '/secure-esignature',
  '/security',
  '/esignature-legality',
  '/how-it-works',
  '/pricing',
  '/contact',
  '/trust',
  '/about',
  '/what-is-esignature',
])

/** Parent crumb for industry pages under /esignature/… */
const ESIGNATURE_HUB_BREADCRUMB = {
  labelKey: 'seoLanding.esignature.breadcrumb',
  hrefLogical: '/esignature',
}

const TEMPLATES_HUB_BREADCRUMB = {
  labelKey: 'seoLanding.templates.breadcrumb',
  hrefLogical: '/templates',
}

export function normalizePathname(pathname) {
  if (!pathname || typeof pathname !== 'string') return '/'
  let p = pathname.trim()
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)
  return p || '/'
}

export function isSeoLandingPathname(pathname) {
  return SEO_LANDING_PATHS.has(normalizePathname(pathname))
}

/** Logical path after legacy alias normalization (e.g. /esignature-legal → /esignature-legality). */
export function getCanonicalPathname(pathname) {
  return rewriteLegacyMarketingLogicalPath(normalizePathname(pathname))
}

/** @returns {{ mode: 'list' } | { mode: 'post', slug: string } | null} */
export function parseBlogPath(pathname) {
  const p = normalizePathname(toLogicalPathname(pathname))
  if (p === '/blog') return { mode: 'list' }
  if (p.startsWith('/blog/')) {
    const slug = p.slice(6)
    if (slug && !slug.includes('/')) return { mode: 'post', slug }
  }
  return null
}

/** i18n keys + DOM id for each SEO article (except shared labels). */
export const SEO_ARTICLE_PAGE_KEYS = {
  '/esignature': {
    htmlId: 'esignature-top',
    breadcrumbKey: 'seoLanding.esignature.breadcrumb',
    titleKey: 'seoLanding.esignature.title',
    leadKey: 'seoLanding.esignature.lead',
    bodyKeys: ['seoLanding.esignature.p1', 'seoLanding.esignature.p2', 'seoLanding.esignature.p3'],
    faqHeadingKey: 'seoLanding.esignature.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/what-is-esignature', labelKey: 'footer.linkWhatIsEsign' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/how-to-sign-documents-online', labelKey: 'footer.linkHowSignOnline' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/esignature/real-estate': {
    htmlId: 'esignature-real-estate-top',
    breadcrumbPredecessor: ESIGNATURE_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.esignRe.breadcrumb',
    titleKey: 'seoLanding.esignRe.title',
    leadKey: 'seoLanding.esignRe.lead',
    bodyKeys: ['seoLanding.esignRe.p1', 'seoLanding.esignRe.p2', 'seoLanding.esignRe.p3'],
    faqHeadingKey: 'seoLanding.esignature.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
  '/esignature/accountants': {
    htmlId: 'esignature-accountants-top',
    breadcrumbPredecessor: ESIGNATURE_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.esignAcct.breadcrumb',
    titleKey: 'seoLanding.esignAcct.title',
    leadKey: 'seoLanding.esignAcct.lead',
    bodyKeys: ['seoLanding.esignAcct.p1', 'seoLanding.esignAcct.p2', 'seoLanding.esignAcct.p3'],
    faqHeadingKey: 'seoLanding.esignature.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
  '/esignature/hr': {
    htmlId: 'esignature-hr-top',
    breadcrumbPredecessor: ESIGNATURE_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.esignHr.breadcrumb',
    titleKey: 'seoLanding.esignHr.title',
    leadKey: 'seoLanding.esignHr.lead',
    bodyKeys: ['seoLanding.esignHr.p1', 'seoLanding.esignHr.p2', 'seoLanding.esignHr.p3'],
    faqHeadingKey: 'seoLanding.esignature.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
  '/esignature/agencies': {
    htmlId: 'esignature-agencies-top',
    breadcrumbPredecessor: ESIGNATURE_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.esignAgency.breadcrumb',
    titleKey: 'seoLanding.esignAgency.title',
    leadKey: 'seoLanding.esignAgency.lead',
    bodyKeys: ['seoLanding.esignAgency.p1', 'seoLanding.esignAgency.p2', 'seoLanding.esignAgency.p3'],
    faqHeadingKey: 'seoLanding.esignature.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
  '/docusign-alternative': {
    htmlId: 'docusign-alternative-top',
    breadcrumbKey: 'docusignAlt.breadcrumb',
    titleKey: 'docusignAlt.title',
    leadKey: 'docusignAlt.lead',
    sectionBlocks: [
      {
        titleKey: 'docusignAlt.secOutcomesTitle',
        bodyKeys: ['docusignAlt.secOutcomesP1', 'docusignAlt.secOutcomesP2'],
      },
      {
        titleKey: 'docusignAlt.secFitTitle',
        bodyKeys: ['docusignAlt.secFitP1', 'docusignAlt.secFitP2'],
      },
      {
        titleKey: 'docusignAlt.secTotalCostTitle',
        bodyKeys: ['docusignAlt.secTotalCostP1', 'docusignAlt.secTotalCostP2'],
      },
      {
        titleKey: 'docusignAlt.secVerifyTitle',
        bodyKeys: ['docusignAlt.secVerifyP1', 'docusignAlt.secVerifyP2'],
      },
      {
        titleKey: 'docusignAlt.secAudienceTitle',
        bodyKeys: ['docusignAlt.secAudienceP1', 'docusignAlt.secAudienceP2'],
        bulletKeys: [
          'docusignAlt.secAudienceLi1',
          'docusignAlt.secAudienceLi2',
          'docusignAlt.secAudienceLi3',
          'docusignAlt.secAudienceLi4',
        ],
      },
      {
        titleKey: 'docusignAlt.secTryTitle',
        bodyKeys: ['docusignAlt.secTryP1', 'docusignAlt.secTryP2'],
      },
    ],
    faqHeadingKey: 'docusignAlt.faqHeading',
    ctaKey: 'docusignAlt.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'docusignAlt.pricingLink',
    showCompareTableAfterBody: true,
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/how-to-sign-documents-online', labelKey: 'footer.linkHowSignOnline' },
      { hrefLogical: '/free-contract-templates', labelKey: 'footer.linkFreeContractTemplates' },
      { hrefLogical: '/esignature-for-freelancers', labelKey: 'footer.linkEsignFreelancers' },
      { hrefLogical: '/secure-esignature', labelKey: 'footer.linkSecureEsign' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/about', labelKey: 'nav.aboutUs' },
      { hrefLogical: '/what-is-esignature', labelKey: 'footer.linkWhatIsEsign' },
    ],
  },
  '/docusign-vs-prime-document-sign': {
    htmlId: 'docusign-vs-prime-top',
    breadcrumbKey: 'seoLanding.docuVs.breadcrumb',
    titleKey: 'seoLanding.docuVs.title',
    bodyKeys: [],
    faqHeadingKey: 'seoLanding.docuVs.faqHeading',
    ctaKey: 'docusignAlt.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'docusignAlt.pricingLink',
    showCompareTableAfterBody: true,
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/how-to-sign-documents-online', labelKey: 'footer.linkHowSignOnline' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/free-esignature': {
    htmlId: 'free-esignature-top',
    breadcrumbKey: 'seoLanding.freeEsign.breadcrumb',
    titleKey: 'seoLanding.freeEsign.title',
    leadKey: 'seoLanding.freeEsign.lead',
    bodyKeys: ['seoLanding.freeEsign.p1', 'seoLanding.freeEsign.p2', 'seoLanding.freeEsign.p3'],
    faqHeadingKey: 'seoLanding.freeEsign.faqHeading',
    ctaKey: 'seoLanding.freeEsign.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.freeEsign.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-to-sign-documents-online', labelKey: 'footer.linkHowSignOnline' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/templates': {
    htmlId: 'templates-top',
    breadcrumbKey: 'seoLanding.templates.breadcrumb',
    titleKey: 'seoLanding.templates.title',
    leadKey: 'seoLanding.templates.lead',
    bodyKeys: ['seoLanding.templates.p1', 'seoLanding.templates.p2', 'seoLanding.templates.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/free-contract-templates', labelKey: 'footer.linkFreeContractTemplates' },
      { hrefLogical: '/how-to-sign-documents-online', labelKey: 'footer.linkHowSignOnline' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/templates/nda': {
    htmlId: 'templates-nda-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplNda.breadcrumb',
    titleKey: 'seoLanding.tplNda.title',
    leadKey: 'seoLanding.tplNda.lead',
    bodyKeys: ['seoLanding.tplNda.p1', 'seoLanding.tplNda.p2', 'seoLanding.tplNda.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/contract': {
    htmlId: 'templates-contract-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplContract.breadcrumb',
    titleKey: 'seoLanding.tplContract.title',
    leadKey: 'seoLanding.tplContract.lead',
    bodyKeys: ['seoLanding.tplContract.p1', 'seoLanding.tplContract.p2', 'seoLanding.tplContract.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/freelance-agreement': {
    htmlId: 'templates-freelance-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplFreelance.breadcrumb',
    titleKey: 'seoLanding.tplFreelance.title',
    leadKey: 'seoLanding.tplFreelance.lead',
    bodyKeys: ['seoLanding.tplFreelance.p1', 'seoLanding.tplFreelance.p2', 'seoLanding.tplFreelance.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/lease-agreement': {
    htmlId: 'templates-lease-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplLease.breadcrumb',
    titleKey: 'seoLanding.tplLease.title',
    leadKey: 'seoLanding.tplLease.lead',
    bodyKeys: ['seoLanding.tplLease.p1', 'seoLanding.tplLease.p2', 'seoLanding.tplLease.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/service-agreement': {
    htmlId: 'templates-service-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplService.breadcrumb',
    titleKey: 'seoLanding.tplService.title',
    leadKey: 'seoLanding.tplService.lead',
    bodyKeys: ['seoLanding.tplService.p1', 'seoLanding.tplService.p2', 'seoLanding.tplService.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/independent-contractor': {
    htmlId: 'templates-contractor-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplContractor.breadcrumb',
    titleKey: 'seoLanding.tplContractor.title',
    leadKey: 'seoLanding.tplContractor.lead',
    bodyKeys: ['seoLanding.tplContractor.p1', 'seoLanding.tplContractor.p2', 'seoLanding.tplContractor.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/purchase-order': {
    htmlId: 'templates-po-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplPo.breadcrumb',
    titleKey: 'seoLanding.tplPo.title',
    leadKey: 'seoLanding.tplPo.lead',
    bodyKeys: ['seoLanding.tplPo.p1', 'seoLanding.tplPo.p2', 'seoLanding.tplPo.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/templates/photo-release': {
    htmlId: 'templates-photo-release-top',
    breadcrumbPredecessor: TEMPLATES_HUB_BREADCRUMB,
    breadcrumbKey: 'seoLanding.tplPhotoRelease.breadcrumb',
    titleKey: 'seoLanding.tplPhotoRelease.title',
    leadKey: 'seoLanding.tplPhotoRelease.lead',
    bodyKeys: ['seoLanding.tplPhotoRelease.p1', 'seoLanding.tplPhotoRelease.p2', 'seoLanding.tplPhotoRelease.p3'],
    faqHeadingKey: 'seoLanding.templates.faqHeading',
    ctaKey: 'seoLanding.templates.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.templates.pricingLink',
  },
  '/esignature-australia': {
    htmlId: 'esignature-australia-top',
    breadcrumbKey: 'seoLanding.esignAu.breadcrumb',
    titleKey: 'seoLanding.esignAu.title',
    leadKey: 'seoLanding.esignAu.lead',
    bodyKeys: ['seoLanding.esignAu.p1', 'seoLanding.esignAu.p2', 'seoLanding.esignAu.p3'],
    faqHeadingKey: 'seoLanding.esignAu.faqHeading',
    ctaKey: 'seoLanding.esignAu.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignAu.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/security': {
    htmlId: 'security-top',
    breadcrumbKey: 'seoLanding.security.breadcrumb',
    titleKey: 'seoLanding.security.title',
    leadKey: 'seoLanding.security.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.security.secTlsTitle',
        bodyKeys: ['seoLanding.security.secTlsP1', 'seoLanding.security.secTlsP2'],
      },
      {
        titleKey: 'seoLanding.security.secIsolationTitle',
        bodyKeys: ['seoLanding.security.secIsolationP1', 'seoLanding.security.secIsolationP2'],
      },
      {
        titleKey: 'seoLanding.security.secLinksTitle',
        bodyKeys: ['seoLanding.security.secLinksP1', 'seoLanding.security.secLinksP2'],
      },
      {
        titleKey: 'seoLanding.security.secAuditTitle',
        bodyKeys: ['seoLanding.security.secAuditP1', 'seoLanding.security.secAuditP2'],
      },
      {
        titleKey: 'seoLanding.security.secPrivacyTitle',
        bodyKeys: ['seoLanding.security.secPrivacyP1', 'seoLanding.security.secPrivacyP2'],
      },
      {
        titleKey: 'seoLanding.security.secOperationsTitle',
        bodyKeys: ['seoLanding.security.secOperationsP1', 'seoLanding.security.secOperationsP2'],
        bulletKeys: [
          'seoLanding.security.secOperationsLi1',
          'seoLanding.security.secOperationsLi2',
          'seoLanding.security.secOperationsLi3',
        ],
      },
      {
        titleKey: 'seoLanding.security.secBuyerTitle',
        bodyKeys: ['seoLanding.security.secBuyerP1', 'seoLanding.security.secBuyerP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.security.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/secure-esignature', labelKey: 'footer.linkSecureEsign' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/about', labelKey: 'nav.aboutUs' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/why-primedocumentsign': {
    htmlId: 'why-prime-documentsign-top',
    breadcrumbKey: 'seoLanding.whyPrime.breadcrumb',
    titleKey: 'seoLanding.whyPrime.title',
    leadKey: 'seoLanding.whyPrime.lead',
    bodyKeys: [
      'seoLanding.whyPrime.p1',
      'seoLanding.whyPrime.p2',
      'seoLanding.whyPrime.p3',
      'seoLanding.whyPrime.p4',
      'seoLanding.whyPrime.p5',
    ],
    bodyKeysPreline: ['seoLanding.whyPrime.p3'],
    faqHeadingKey: 'seoLanding.whyPrime.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/about': {
    htmlId: 'about-standalone-top',
    breadcrumbKey: 'seoLanding.aboutTrust.breadcrumb',
    titleKey: 'seoLanding.aboutTrust.title',
    leadKey: 'seoLanding.aboutTrust.lead',
    bodyKeys: [
      'seoLanding.aboutTrust.p1',
      'seoLanding.aboutTrust.p2',
      'seoLanding.aboutTrust.p3',
      'seoLanding.aboutTrust.p4',
      'seoLanding.aboutTrust.p5',
      'seoLanding.aboutTrust.p6',
    ],
    faqHeadingKey: 'seoLanding.aboutTrust.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
      { hrefLogical: '/what-is-esignature', labelKey: 'footer.linkWhatIsEsign' },
    ],
  },
  '/trust': {
    htmlId: 'trust-master-top',
    breadcrumbKey: 'seoLanding.trustMaster.breadcrumb',
    titleKey: 'seoLanding.trustMaster.title',
    leadKey: 'seoLanding.trustMaster.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.trustMaster.secSecureTitle',
        bodyKeys: ['seoLanding.trustMaster.secSecureP1', 'seoLanding.trustMaster.secSecureP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secEncryptTitle',
        bodyKeys: ['seoLanding.trustMaster.secEncryptP1', 'seoLanding.trustMaster.secEncryptP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secAuditTitle',
        bodyKeys: ['seoLanding.trustMaster.secAuditP1', 'seoLanding.trustMaster.secAuditP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secLegalTitle',
        bodyKeys: ['seoLanding.trustMaster.secLegalP1', 'seoLanding.trustMaster.secLegalP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secPrivacyTitle',
        bodyKeys: ['seoLanding.trustMaster.secPrivacyP1', 'seoLanding.trustMaster.secPrivacyP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secCompanyTitle',
        bodyKeys: ['seoLanding.trustMaster.secCompanyP1', 'seoLanding.trustMaster.secCompanyP2'],
      },
      {
        titleKey: 'seoLanding.trustMaster.secListingTitle',
        bodyKeys: ['seoLanding.trustMaster.secListingP1'],
      },
    ],
    faqHeadingKey: 'seoLanding.trustMaster.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/about', labelKey: 'nav.aboutUs' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
      { hrefLogical: '/what-is-esignature', labelKey: 'footer.linkWhatIsEsign' },
    ],
  },
  '/product': {
    htmlId: 'product-reference-top',
    breadcrumbKey: 'seoLanding.productRef.breadcrumb',
    titleKey: 'seoLanding.productRef.title',
    leadKey: 'seoLanding.productRef.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.productRef.secWhatTitle',
        bodyKeys: ['seoLanding.productRef.secWhatP1', 'seoLanding.productRef.secWhatP2'],
      },
      {
        titleKey: 'seoLanding.productRef.secFeaturesTitle',
        bodyKeys: ['seoLanding.productRef.secFeaturesP1', 'seoLanding.productRef.secFeaturesP2'],
      },
      {
        titleKey: 'seoLanding.productRef.secComplianceTitle',
        bodyKeys: ['seoLanding.productRef.secComplianceP1', 'seoLanding.productRef.secComplianceP2'],
      },
      {
        titleKey: 'seoLanding.productRef.secPricingTitle',
        bodyKeys: ['seoLanding.productRef.secPricingP1', 'seoLanding.productRef.secPricingP2'],
      },
      {
        titleKey: 'seoLanding.productRef.secUseCasesTitle',
        bodyKeys: ['seoLanding.productRef.secUseCasesP1', 'seoLanding.productRef.secUseCasesP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.productRef.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/what-is-esignature', labelKey: 'footer.linkWhatIsEsign' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/alternatives': {
    htmlId: 'alternatives-hub-top',
    breadcrumbKey: 'seoLanding.altHub.breadcrumb',
    titleKey: 'seoLanding.altHub.title',
    leadKey: 'seoLanding.altHub.lead',
    bodyKeys: ['seoLanding.altHub.p1', 'seoLanding.altHub.p2', 'seoLanding.altHub.p3'],
    faqHeadingKey: 'seoLanding.altHub.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/how-to-sign-documents-online', labelKey: 'footer.linkHowSignOnline' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/what-is-esignature': {
    htmlId: 'what-is-esignature-top',
    breadcrumbKey: 'seoLanding.whatEsign.breadcrumb',
    titleKey: 'seoLanding.whatEsign.title',
    leadKey: 'seoLanding.whatEsign.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.whatEsign.secDefTitle',
        bodyKeys: ['seoLanding.whatEsign.secDefP1'],
      },
      {
        titleKey: 'seoLanding.whatEsign.secHowTitle',
        bodyKeys: ['seoLanding.whatEsign.secHowP1', 'seoLanding.whatEsign.secHowP2'],
      },
      {
        titleKey: 'seoLanding.whatEsign.secLegalTitle',
        bodyKeys: ['seoLanding.whatEsign.secLegalP1', 'seoLanding.whatEsign.secLegalP2'],
      },
      {
        titleKey: 'seoLanding.whatEsign.secBenefitsTitle',
        bodyKeys: ['seoLanding.whatEsign.secBenefitsP1', 'seoLanding.whatEsign.secBenefitsP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.whatEsign.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/how-to-sign-documents-online', labelKey: 'footer.linkHowSignOnline' },
      { hrefLogical: '/secure-esignature', labelKey: 'footer.linkSecureEsign' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/reviews': {
    htmlId: 'reviews-top',
    breadcrumbKey: 'seoLanding.reviewsPage.breadcrumb',
    titleKey: 'seoLanding.reviewsPage.title',
    leadKey: 'seoLanding.reviewsPage.lead',
    bodyKeys: [
      'seoLanding.reviewsPage.p1',
      'seoLanding.reviewsPage.p2',
      'seoLanding.reviewsPage.p3',
      'seoLanding.reviewsPage.p4',
    ],
    faqHeadingKey: 'seoLanding.reviewsPage.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/about', labelKey: 'nav.aboutUs' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/how-to-sign-documents-online': {
    htmlId: 'how-sign-online-top',
    breadcrumbKey: 'seoLanding.howSignOnline.breadcrumb',
    titleKey: 'seoLanding.howSignOnline.title',
    leadKey: 'seoLanding.howSignOnline.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.howSignOnline.secPrepareTitle',
        bodyKeys: ['seoLanding.howSignOnline.secPrepareP1', 'seoLanding.howSignOnline.secPrepareP2'],
      },
      {
        titleKey: 'seoLanding.howSignOnline.secSendTitle',
        bodyKeys: ['seoLanding.howSignOnline.secSendP1', 'seoLanding.howSignOnline.secSendP2'],
      },
      {
        titleKey: 'seoLanding.howSignOnline.secSignTitle',
        bodyKeys: ['seoLanding.howSignOnline.secSignP1', 'seoLanding.howSignOnline.secSignP2'],
      },
      {
        titleKey: 'seoLanding.howSignOnline.secAfterTitle',
        bodyKeys: ['seoLanding.howSignOnline.secAfterP1', 'seoLanding.howSignOnline.secAfterP2'],
      },
      {
        titleKey: 'seoLanding.howSignOnline.secChecklistTitle',
        bodyKeys: ['seoLanding.howSignOnline.secChecklistIntro'],
        bulletKeys: [
          'seoLanding.howSignOnline.secChecklistLi1',
          'seoLanding.howSignOnline.secChecklistLi2',
          'seoLanding.howSignOnline.secChecklistLi3',
          'seoLanding.howSignOnline.secChecklistLi4',
          'seoLanding.howSignOnline.secChecklistLi5',
        ],
      },
      {
        titleKey: 'seoLanding.howSignOnline.secMistakesTitle',
        bodyKeys: ['seoLanding.howSignOnline.secMistakesP1', 'seoLanding.howSignOnline.secMistakesP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.howSignOnline.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/what-is-esignature', labelKey: 'footer.linkWhatIsEsign' },
      { hrefLogical: '/free-contract-templates', labelKey: 'footer.linkFreeContractTemplates' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/free-contract-templates': {
    htmlId: 'free-contract-templates-top',
    breadcrumbKey: 'seoLanding.freeContractTpl.breadcrumb',
    titleKey: 'seoLanding.freeContractTpl.title',
    leadKey: 'seoLanding.freeContractTpl.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.freeContractTpl.secWhatTitle',
        bodyKeys: ['seoLanding.freeContractTpl.secWhatP1', 'seoLanding.freeContractTpl.secWhatP2'],
      },
      {
        titleKey: 'seoLanding.freeContractTpl.secWorkflowTitle',
        bodyKeys: ['seoLanding.freeContractTpl.secWorkflowP1', 'seoLanding.freeContractTpl.secWorkflowP2'],
      },
      {
        titleKey: 'seoLanding.freeContractTpl.secLibraryTitle',
        bodyKeys: ['seoLanding.freeContractTpl.secLibraryP1', 'seoLanding.freeContractTpl.secLibraryP2'],
      },
      {
        titleKey: 'seoLanding.freeContractTpl.secUseCasesTitle',
        bodyKeys: ['seoLanding.freeContractTpl.secUseCasesP1'],
        bulletKeys: [
          'seoLanding.freeContractTpl.secUseCasesLi1',
          'seoLanding.freeContractTpl.secUseCasesLi2',
          'seoLanding.freeContractTpl.secUseCasesLi3',
          'seoLanding.freeContractTpl.secUseCasesLi4',
          'seoLanding.freeContractTpl.secUseCasesLi5',
        ],
      },
      {
        titleKey: 'seoLanding.freeContractTpl.secLandlordsTitle',
        bodyKeys: ['seoLanding.freeContractTpl.secLandlordsP1', 'seoLanding.freeContractTpl.secLandlordsP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.freeContractTpl.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/how-to-sign-documents-online', labelKey: 'footer.linkHowSignOnline' },
      { hrefLogical: '/esignature-for-freelancers', labelKey: 'footer.linkEsignFreelancers' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/esignature-for-freelancers': {
    htmlId: 'esign-freelancers-top',
    breadcrumbKey: 'seoLanding.esignFreelancers.breadcrumb',
    titleKey: 'seoLanding.esignFreelancers.title',
    leadKey: 'seoLanding.esignFreelancers.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.esignFreelancers.secSpeedTitle',
        bodyKeys: ['seoLanding.esignFreelancers.secSpeedP1', 'seoLanding.esignFreelancers.secSpeedP2'],
      },
      {
        titleKey: 'seoLanding.esignFreelancers.secClientsTitle',
        bodyKeys: ['seoLanding.esignFreelancers.secClientsP1', 'seoLanding.esignFreelancers.secClientsP2'],
      },
      {
        titleKey: 'seoLanding.esignFreelancers.secMoneyTitle',
        bodyKeys: ['seoLanding.esignFreelancers.secMoneyP1', 'seoLanding.esignFreelancers.secMoneyP2'],
      },
      {
        titleKey: 'seoLanding.esignFreelancers.secStackTitle',
        bodyKeys: ['seoLanding.esignFreelancers.secStackP1', 'seoLanding.esignFreelancers.secStackP2'],
        bulletKeys: [
          'seoLanding.esignFreelancers.secStackLi1',
          'seoLanding.esignFreelancers.secStackLi2',
          'seoLanding.esignFreelancers.secStackLi3',
        ],
      },
      {
        titleKey: 'seoLanding.esignFreelancers.secAgenciesTitle',
        bodyKeys: ['seoLanding.esignFreelancers.secAgenciesP1', 'seoLanding.esignFreelancers.secAgenciesP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.esignFreelancers.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/how-to-sign-documents-online', labelKey: 'footer.linkHowSignOnline' },
      { hrefLogical: '/free-contract-templates', labelKey: 'footer.linkFreeContractTemplates' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/secure-esignature': {
    htmlId: 'secure-esignature-top',
    breadcrumbKey: 'seoLanding.secureEsign.breadcrumb',
    titleKey: 'seoLanding.secureEsign.title',
    leadKey: 'seoLanding.secureEsign.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.secureEsign.secSessionTitle',
        bodyKeys: ['seoLanding.secureEsign.secSessionP1', 'seoLanding.secureEsign.secSessionP2'],
      },
      {
        titleKey: 'seoLanding.secureEsign.secEvidenceTitle',
        bodyKeys: ['seoLanding.secureEsign.secEvidenceP1', 'seoLanding.secureEsign.secEvidenceP2'],
      },
      {
        titleKey: 'seoLanding.secureEsign.secRecipientTitle',
        bodyKeys: ['seoLanding.secureEsign.secRecipientP1', 'seoLanding.secureEsign.secRecipientP2'],
      },
      {
        titleKey: 'seoLanding.secureEsign.secChecklistTitle',
        bodyKeys: ['seoLanding.secureEsign.secChecklistIntro'],
        bulletKeys: [
          'seoLanding.secureEsign.secChecklistLi1',
          'seoLanding.secureEsign.secChecklistLi2',
          'seoLanding.secureEsign.secChecklistLi3',
          'seoLanding.secureEsign.secChecklistLi4',
        ],
      },
      {
        titleKey: 'seoLanding.secureEsign.secHonestLimitsTitle',
        bodyKeys: ['seoLanding.secureEsign.secHonestLimitsP1', 'seoLanding.secureEsign.secHonestLimitsP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.secureEsign.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/what-is-esignature', labelKey: 'footer.linkWhatIsEsign' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/esignature-legality': {
    htmlId: 'esignature-legality-top',
    breadcrumbKey: 'seoLanding.esignLegalityPage.breadcrumb',
    titleKey: 'seoLanding.esignLegalityPage.title',
    leadKey: 'seoLanding.esignLegalityPage.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.esignLegalityPage.secFrameworkTitle',
        bodyKeys: ['seoLanding.esignLegalityPage.secFrameworkP1', 'seoLanding.esignLegalityPage.secFrameworkP2'],
      },
      {
        titleKey: 'seoLanding.esignLegalityPage.secProcessTitle',
        bodyKeys: ['seoLanding.esignLegalityPage.secProcessP1', 'seoLanding.esignLegalityPage.secProcessP2'],
      },
      {
        titleKey: 'seoLanding.esignLegalityPage.secLimitsTitle',
        bodyKeys: ['seoLanding.esignLegalityPage.secLimitsP1', 'seoLanding.esignLegalityPage.secLimitsP2'],
      },
      {
        titleKey: 'seoLanding.esignLegalityPage.secBindingTitle',
        bodyKeys: ['seoLanding.esignLegalityPage.secBindingP1', 'seoLanding.esignLegalityPage.secBindingP2'],
        bulletKeys: [
          'seoLanding.esignLegalityPage.secBindingLi1',
          'seoLanding.esignLegalityPage.secBindingLi2',
          'seoLanding.esignLegalityPage.secBindingLi3',
        ],
      },
      {
        titleKey: 'seoLanding.esignLegalityPage.secRecordsTitle',
        bodyKeys: ['seoLanding.esignLegalityPage.secRecordsP1', 'seoLanding.esignLegalityPage.secRecordsP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.esignLegalityPage.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/what-is-esignature', labelKey: 'footer.linkWhatIsEsign' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/secure-esignature', labelKey: 'footer.linkSecureEsign' },
      { hrefLogical: '/how-to-sign-documents-online', labelKey: 'footer.linkHowSignOnline' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
    ],
  },
  '/how-it-works': {
    htmlId: 'how-it-works-top',
    breadcrumbKey: 'seoLanding.howItWorksPage.breadcrumb',
    titleKey: 'seoLanding.howItWorksPage.title',
    leadKey: 'seoLanding.howItWorksPage.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.howItWorksPage.secComposerTitle',
        bodyKeys: ['seoLanding.howItWorksPage.secComposerP1', 'seoLanding.howItWorksPage.secComposerP2'],
      },
      {
        titleKey: 'seoLanding.howItWorksPage.secDeliveryTitle',
        bodyKeys: ['seoLanding.howItWorksPage.secDeliveryP1', 'seoLanding.howItWorksPage.secDeliveryP2'],
      },
      {
        titleKey: 'seoLanding.howItWorksPage.secPdfTitle',
        bodyKeys: ['seoLanding.howItWorksPage.secPdfP1', 'seoLanding.howItWorksPage.secPdfP2'],
      },
      {
        titleKey: 'seoLanding.howItWorksPage.secRolesTitle',
        bodyKeys: ['seoLanding.howItWorksPage.secRolesP1'],
        bulletKeys: [
          'seoLanding.howItWorksPage.secRolesLi1',
          'seoLanding.howItWorksPage.secRolesLi2',
          'seoLanding.howItWorksPage.secRolesLi3',
          'seoLanding.howItWorksPage.secRolesLi4',
        ],
      },
      {
        titleKey: 'seoLanding.howItWorksPage.secScaleTitle',
        bodyKeys: ['seoLanding.howItWorksPage.secScaleP1', 'seoLanding.howItWorksPage.secScaleP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.howItWorksPage.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/how-to-sign-documents-online', labelKey: 'footer.linkHowSignOnline' },
      { hrefLogical: '/free-contract-templates', labelKey: 'footer.linkFreeContractTemplates' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
    ],
  },
  '/pricing': {
    htmlId: 'pricing-seo-top',
    breadcrumbKey: 'seoLanding.pricingPage.breadcrumb',
    titleKey: 'seoLanding.pricingPage.title',
    leadKey: 'seoLanding.pricingPage.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.pricingPage.secPlansTitle',
        bodyKeys: ['seoLanding.pricingPage.secPlansP1', 'seoLanding.pricingPage.secPlansP2'],
      },
      {
        titleKey: 'seoLanding.pricingPage.secPaygoTitle',
        bodyKeys: ['seoLanding.pricingPage.secPaygoP1', 'seoLanding.pricingPage.secPaygoP2'],
      },
      {
        titleKey: 'seoLanding.pricingPage.secChooseTitle',
        bodyKeys: ['seoLanding.pricingPage.secChooseP1', 'seoLanding.pricingPage.secChooseP2'],
      },
      {
        titleKey: 'seoLanding.pricingPage.secValueTitle',
        bodyKeys: ['seoLanding.pricingPage.secValueP1'],
        bulletKeys: [
          'seoLanding.pricingPage.secValueLi1',
          'seoLanding.pricingPage.secValueLi2',
          'seoLanding.pricingPage.secValueLi3',
          'seoLanding.pricingPage.secValueLi4',
        ],
      },
      {
        titleKey: 'seoLanding.pricingPage.secEnterpriseTitle',
        bodyKeys: ['seoLanding.pricingPage.secEnterpriseP1', 'seoLanding.pricingPage.secEnterpriseP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.pricingPage.faqHeading',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '#pricing',
    pricingLabelKey: 'seoLanding.pricingPage.jumpToCalculator',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/contact', labelKey: 'footer.linkContactPage' },
      { hrefLogical: '/free-contract-templates', labelKey: 'footer.linkFreeContractTemplates' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
    ],
  },
  '/contact': {
    htmlId: 'contact-seo-top',
    breadcrumbKey: 'seoLanding.contactPage.breadcrumb',
    titleKey: 'seoLanding.contactPage.title',
    leadKey: 'seoLanding.contactPage.lead',
    sectionBlocks: [
      {
        titleKey: 'seoLanding.contactPage.secSalesTitle',
        bodyKeys: ['seoLanding.contactPage.secSalesP1', 'seoLanding.contactPage.secSalesP2'],
      },
      {
        titleKey: 'seoLanding.contactPage.secSupportTitle',
        bodyKeys: ['seoLanding.contactPage.secSupportP1', 'seoLanding.contactPage.secSupportP2'],
      },
      {
        titleKey: 'seoLanding.contactPage.secTrustTitle',
        bodyKeys: ['seoLanding.contactPage.secTrustP1', 'seoLanding.contactPage.secTrustP2'],
      },
      {
        titleKey: 'seoLanding.contactPage.secResponseTitle',
        bodyKeys: ['seoLanding.contactPage.secResponseIntro'],
        bulletKeys: [
          'seoLanding.contactPage.secResponseLi1',
          'seoLanding.contactPage.secResponseLi2',
          'seoLanding.contactPage.secResponseLi3',
        ],
      },
      {
        titleKey: 'seoLanding.contactPage.secHelpTitle',
        bodyKeys: ['seoLanding.contactPage.secHelpP1', 'seoLanding.contactPage.secHelpP2'],
      },
    ],
    faqHeadingKey: 'seoLanding.contactPage.faqHeading',
    ctaKey: 'seoLanding.contactPage.ctaForm',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.contactPage.ctaViewPricing',
    homePrimaryHash: 'contact',
    crossLinks: [
      { hrefLogical: '/', labelKey: 'nav.home' },
      { hrefLogical: '/docusign-alternative', labelKey: 'footer.docusignAlt' },
      { hrefLogical: '/how-it-works', labelKey: 'footer.linkHowItWorksPage' },
      { hrefLogical: '/pricing', labelKey: 'footer.linkPricingPage' },
      { hrefLogical: '/trust', labelKey: 'footer.linkTrust' },
      { hrefLogical: '/about', labelKey: 'nav.aboutUs' },
      { hrefLogical: '/security', labelKey: 'footer.linkSecurity' },
      { hrefLogical: '/esignature-legality', labelKey: 'footer.linkEsignLegality' },
    ],
  },
  '/terms': {
    htmlId: 'terms-of-service-top',
    breadcrumbKey: 'footer.terms',
    legalPage: 'terms',
    updatedLabelKey: 'legal.termsLastUpdated',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
  '/privacy': {
    htmlId: 'privacy-policy-top',
    breadcrumbKey: 'footer.privacy',
    legalPage: 'privacy',
    updatedLabelKey: 'legal.privacyLastUpdated',
    ctaKey: 'seoLanding.esignature.cta',
    pricingHref: '/pricing',
    pricingLabelKey: 'seoLanding.esignature.pricingLink',
  },
}

/** Long-form marketing article (may be indexable or legacy / noindex). */
export function isSeoArticlePathname(pathname) {
  const p = normalizePathname(pathname)
  return Object.prototype.hasOwnProperty.call(SEO_ARTICLE_PAGE_KEYS, p)
}

const ESIGNATURE_HUB_FAQ = [
  { qKey: 'faq.seoWhatIsESignatureQ', aKey: 'faq.seoWhatIsESignatureA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA' },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.aiWhatIsESignSoftwareForQ', aKey: 'faq.aiWhatIsESignSoftwareForA', multiParagraph: true },
  { qKey: 'faq.aiHowMuchDoesESignCostQ', aKey: 'faq.aiHowMuchDoesESignCostA', multiParagraph: true },
  { qKey: 'faq.aiHowChooseESignProviderQ', aKey: 'faq.aiHowChooseESignProviderA', multiParagraph: true },
]

function freeEsignFaqs(tierPricing) {
  const ot = tierPricing.oneTimePrice
  return [
    { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
    { qKey: 'faq.aiFreeESignToolQ', aKey: 'faq.aiFreeESignToolA', multiParagraph: true, params: { oneTimePrice: ot } },
    { qKey: 'faq.aiSignPdfOnlineFreeQ', aKey: 'faq.aiSignPdfOnlineFreeA', multiParagraph: true },
    { qKey: 'faq.recipientAccountQ', aKey: 'faq.recipientAccountA' },
    { qKey: 'faq.confirmationEmailQ', aKey: 'faq.confirmationEmailA' },
    { qKey: 'faq.aiAffordableSignOnlineQ', aKey: 'faq.aiAffordableSignOnlineA', multiParagraph: true },
  ]
}

/** Core template workflow — reused on every /templates/* subpage (after page-specific extras). */
const TEMPLATES_PAGE_FAQ = [
  { qKey: 'seoLanding.templates.faqWhyQ', aKey: 'seoLanding.templates.faqWhyA', multiParagraph: true },
  { qKey: 'seoLanding.templates.faqStarterQ', aKey: 'seoLanding.templates.faqStarterA', multiParagraph: true },
  { qKey: 'faq.reuseTemplatesQ', aKey: 'faq.reuseTemplatesA' },
  { qKey: 'faq.saveTemplateQ', aKey: 'faq.saveTemplateA' },
  { qKey: 'faq.templateLimitsQ', aKey: 'faq.templateLimitsA' },
  { qKey: 'faq.loadTemplateQ', aKey: 'faq.loadTemplateA' },
]

/** Extra items on /templates hub only — richer FAQPage + visible accordion. */
const TEMPLATES_HUB_ONLY_FAQ = [
  { qKey: 'seoLanding.templates.faqSignedPdfQ', aKey: 'seoLanding.templates.faqSignedPdfA', multiParagraph: true },
  { qKey: 'seoLanding.templates.faqMultiSignerQ', aKey: 'seoLanding.templates.faqMultiSignerA', multiParagraph: true },
  { qKey: 'seoLanding.templates.faqNotLegalAdviceQ', aKey: 'seoLanding.templates.faqNotLegalAdviceA', multiParagraph: true },
  { qKey: 'seoLanding.templates.faqUpdateTemplateQ', aKey: 'seoLanding.templates.faqUpdateTemplateA', multiParagraph: true },
]

const TEMPLATES_HUB_FAQ = [...TEMPLATES_PAGE_FAQ, ...TEMPLATES_HUB_ONLY_FAQ]

const TPL_NDA_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplNda.faqMutualQ', aKey: 'seoLanding.tplNda.faqMutualA', multiParagraph: true },
  { qKey: 'seoLanding.tplNda.faqTradeSecretsQ', aKey: 'seoLanding.tplNda.faqTradeSecretsA', multiParagraph: true },
]
const TPL_CONTRACT_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplContract.faqMsaSowQ', aKey: 'seoLanding.tplContract.faqMsaSowA', multiParagraph: true },
  { qKey: 'seoLanding.tplContract.faqPaymentScheduleQ', aKey: 'seoLanding.tplContract.faqPaymentScheduleA', multiParagraph: true },
]
const TPL_FREELANCE_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplFreelance.faqIpDeliverablesQ', aKey: 'seoLanding.tplFreelance.faqIpDeliverablesA', multiParagraph: true },
  { qKey: 'seoLanding.tplFreelance.faqRetainerQ', aKey: 'seoLanding.tplFreelance.faqRetainerA', multiParagraph: true },
]
const TPL_LEASE_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplLease.faqCosignerQ', aKey: 'seoLanding.tplLease.faqCosignerA', multiParagraph: true },
  { qKey: 'seoLanding.tplLease.faqCommercialVsResidentialQ', aKey: 'seoLanding.tplLease.faqCommercialVsResidentialA', multiParagraph: true },
]
const TPL_SERVICE_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplService.faqSlaQ', aKey: 'seoLanding.tplService.faqSlaA', multiParagraph: true },
  { qKey: 'seoLanding.tplService.faqChangeOrderQ', aKey: 'seoLanding.tplService.faqChangeOrderA', multiParagraph: true },
]
const TPL_CONTRACTOR_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplContractor.faqVsEmployeeQ', aKey: 'seoLanding.tplContractor.faqVsEmployeeA', multiParagraph: true },
  { qKey: 'seoLanding.tplContractor.faqInsuranceQ', aKey: 'seoLanding.tplContractor.faqInsuranceA', multiParagraph: true },
]
const TPL_PO_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplPo.faqVsContractQ', aKey: 'seoLanding.tplPo.faqVsContractA', multiParagraph: true },
  { qKey: 'seoLanding.tplPo.faqApprovalQ', aKey: 'seoLanding.tplPo.faqApprovalA', multiParagraph: true },
]
const TPL_PHOTO_EXTRA_FAQ = [
  { qKey: 'seoLanding.tplPhotoRelease.faqTalentQ', aKey: 'seoLanding.tplPhotoRelease.faqTalentA', multiParagraph: true },
  { qKey: 'seoLanding.tplPhotoRelease.faqCommercialUseQ', aKey: 'seoLanding.tplPhotoRelease.faqCommercialUseA', multiParagraph: true },
]

function templateSubpageFaqWithCore(extra) {
  return [...extra, ...TEMPLATES_PAGE_FAQ]
}

const ESIGN_RE_EXTRA_FAQ = [
  { qKey: 'seoLanding.esignRe.faqDisclosuresQ', aKey: 'seoLanding.esignRe.faqDisclosuresA', multiParagraph: true },
  { qKey: 'seoLanding.esignRe.faqRemoteClosingQ', aKey: 'seoLanding.esignRe.faqRemoteClosingA', multiParagraph: true },
]
const ESIGN_ACCT_EXTRA_FAQ = [
  { qKey: 'seoLanding.esignAcct.faqEngagementLetterQ', aKey: 'seoLanding.esignAcct.faqEngagementLetterA', multiParagraph: true },
  { qKey: 'seoLanding.esignAcct.faqTaxAuthQ', aKey: 'seoLanding.esignAcct.faqTaxAuthA', multiParagraph: true },
]
const ESIGN_HR_EXTRA_FAQ = [
  { qKey: 'seoLanding.esignHr.faqOfferLetterQ', aKey: 'seoLanding.esignHr.faqOfferLetterA', multiParagraph: true },
  { qKey: 'seoLanding.esignHr.faqPolicyAckQ', aKey: 'seoLanding.esignHr.faqPolicyAckA', multiParagraph: true },
]
const ESIGN_AGENCY_EXTRA_FAQ = [
  { qKey: 'seoLanding.esignAgency.faqSowQ', aKey: 'seoLanding.esignAgency.faqSowA', multiParagraph: true },
  { qKey: 'seoLanding.esignAgency.faqChangeOrderQ', aKey: 'seoLanding.esignAgency.faqChangeOrderA', multiParagraph: true },
]

/** “Why us” page — distinct from generic /esignature hub FAQ schema. */
const WHY_PRIME_FAQ = [
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA', multiParagraph: true },
  { qKey: 'faq.aiBestSmallBusinessESignQ', aKey: 'faq.aiBestSmallBusinessESignA', multiParagraph: true },
  { qKey: 'faq.aiFreelancerContractSigningQ', aKey: 'faq.aiFreelancerContractSigningA', multiParagraph: true },
  { qKey: 'faq.primeFeaturesForTeamsQ', aKey: 'faq.primeFeaturesForTeamsA', multiParagraph: true },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
  { qKey: 'faq.aiHowMuchDoesESignCostQ', aKey: 'faq.aiHowMuchDoesESignCostA', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoDocuSignAlternativeQ', aKey: 'faq.seoDocuSignAlternativeA', multiParagraph: true },
  { qKey: 'faq.primeTrustWhoWeAreQ', aKey: 'faq.primeTrustWhoWeAreA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
]

const AU_PAGE_FAQ = [
  { qKey: 'seoLanding.esignAu.faqLegalQ', aKey: 'seoLanding.esignAu.faqLegalA', multiParagraph: true },
  { qKey: 'seoLanding.esignAu.faqEtaQ', aKey: 'seoLanding.esignAu.faqEtaA', multiParagraph: true },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'seoLanding.esignAu.faqBusinessQ', aKey: 'seoLanding.esignAu.faqBusinessA', multiParagraph: true },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
]

/** `/docusign-vs-prime-document-sign` — conversion-focused FAQs + JSON-LD */
const DOCUSIGN_VS_PAGE_FAQ = [
  { qKey: 'faq.docuVs.alternativeQ', aKey: 'faq.docuVs.alternativeA', multiParagraph: true },
  { qKey: 'faq.docuVs.pricingCompareQ', aKey: 'faq.docuVs.pricingCompareA', multiParagraph: true },
  { qKey: 'faq.docuVs.freeSendQ', aKey: 'faq.docuVs.freeSendA', multiParagraph: true },
  { qKey: 'faq.seoMigrateFromDocuSignQ', aKey: 'faq.seoMigrateFromDocuSignA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
]

/** Trust & security — encryption, access control, audit context */
const SECURITY_TRUST_FAQ = [
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.dataSecureQ', aKey: 'faq.dataSecureA' },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.primeTrustWhoWeAreQ', aKey: 'faq.primeTrustWhoWeAreA', multiParagraph: true },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
]

/** Company trust narrative */
const ABOUT_TRUST_FAQ = [
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA' },
  { qKey: 'faq.primeTrustWhoWeAreQ', aKey: 'faq.primeTrustWhoWeAreA', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
  { qKey: 'faq.primeSocialProofQ', aKey: 'faq.primeSocialProofA', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.seoWhoUsesPrimeQ', aKey: 'faq.seoWhoUsesPrimeA' },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
]

/** Master trust hub — security, encryption, audit, legal, privacy, company */
const TRUST_MASTER_FAQ = [
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
  { qKey: 'faq.dataSecureQ', aKey: 'faq.dataSecureA' },
  { qKey: 'faq.primeTrustWhoWeAreQ', aKey: 'faq.primeTrustWhoWeAreA', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
]

const PRODUCT_REFERENCE_FAQ = [
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA' },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
  { qKey: 'faq.aiHowMuchDoesESignCostQ', aKey: 'faq.aiHowMuchDoesESignCostA', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.seoWhoUsesPrimeQ', aKey: 'faq.seoWhoUsesPrimeA' },
]

const ALTERNATIVES_HUB_FAQ = [
  { qKey: 'faq.aiWhatIsDocuSignAlternativeQ', aKey: 'faq.aiWhatIsDocuSignAlternativeA', multiParagraph: true },
  { qKey: 'faq.docuVs.alternativeQ', aKey: 'faq.docuVs.alternativeA', multiParagraph: true },
  { qKey: 'faq.seoMigrateFromDocuSignQ', aKey: 'faq.seoMigrateFromDocuSignA' },
  { qKey: 'faq.aiBestSmallBusinessESignQ', aKey: 'faq.aiBestSmallBusinessESignA', multiParagraph: true },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
]

const WHAT_IS_ESIGN_FAQ = [
  { qKey: 'faq.seoWhatIsESignatureQ', aKey: 'faq.seoWhatIsESignatureA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.aiHowChooseESignProviderQ', aKey: 'faq.aiHowChooseESignProviderA', multiParagraph: true },
]

const REVIEWS_PAGE_FAQ = [
  { qKey: 'faq.aiThirdPartyDirectoryReviewsQ', aKey: 'faq.aiThirdPartyDirectoryReviewsA', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
  { qKey: 'faq.primeSocialProofQ', aKey: 'faq.primeSocialProofA', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory1Q', aKey: 'faq.primeCustomerStory1A', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory2Q', aKey: 'faq.primeCustomerStory2A', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA' },
]

const HOW_SIGN_ONLINE_FAQ = [
  { qKey: 'faq.seoWhatIsESignatureQ', aKey: 'faq.seoWhatIsESignatureA' },
  { qKey: 'faq.aiSignPdfOnlineFreeQ', aKey: 'faq.aiSignPdfOnlineFreeA', multiParagraph: true },
  { qKey: 'faq.recipientAccountQ', aKey: 'faq.recipientAccountA' },
  { qKey: 'faq.confirmationEmailQ', aKey: 'faq.confirmationEmailA' },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
]

const FREE_CONTRACT_TEMPLATES_FAQ = [
  { qKey: 'seoLanding.templates.faqWhyQ', aKey: 'seoLanding.templates.faqWhyA', multiParagraph: true },
  { qKey: 'faq.reuseTemplatesQ', aKey: 'faq.reuseTemplatesA' },
  { qKey: 'faq.templateLimitsQ', aKey: 'faq.templateLimitsA' },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
]

const ESIGN_FREELANCERS_FAQ = [
  { qKey: 'faq.aiFreelancerContractSigningQ', aKey: 'faq.aiFreelancerContractSigningA', multiParagraph: true },
  { qKey: 'faq.aiBestSmallBusinessESignQ', aKey: 'faq.aiBestSmallBusinessESignA', multiParagraph: true },
  { qKey: 'faq.seoDocuSignAlternativeQ', aKey: 'faq.seoDocuSignAlternativeA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
]

const SECURE_ESIGN_FAQ = [
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.dataSecureQ', aKey: 'faq.dataSecureA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
]

const ESIGNATURE_LEGALITY_PAGE_FAQ = [
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.seoWhatIsESignatureQ', aKey: 'faq.seoWhatIsESignatureA' },
  { qKey: 'faq.seoMigrateFromDocuSignQ', aKey: 'faq.seoMigrateFromDocuSignA' },
]

const HOW_IT_WORKS_PAGE_FAQ = [
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA' },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
  { qKey: 'faq.aiHowMuchDoesESignCostQ', aKey: 'faq.aiHowMuchDoesESignCostA', multiParagraph: true },
  { qKey: 'faq.recipientAccountQ', aKey: 'faq.recipientAccountA' },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
]

const PRICING_SEO_PAGE_FAQ = [
  { qKey: 'faq.aiHowMuchDoesESignCostQ', aKey: 'faq.aiHowMuchDoesESignCostA', multiParagraph: true },
  { qKey: 'faq.seoPricingVsEnterpriseQ', aKey: 'faq.seoPricingVsEnterpriseA' },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
  { qKey: 'faq.docuVs.pricingCompareQ', aKey: 'faq.docuVs.pricingCompareA', multiParagraph: true },
  { qKey: 'faq.aiAffordableSignOnlineQ', aKey: 'faq.aiAffordableSignOnlineA', multiParagraph: true },
]

const CONTACT_PAGE_FAQ = [
  { qKey: 'faq.primeCustomerStory1Q', aKey: 'faq.primeCustomerStory1A', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA' },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
]

/**
 * FAQs shown in the top article + FAQPage JSON-LD for that URL.
 * @param {object} docuSignAltFaqItems - from App (DOCUSIGN_ALT_FAQ_ITEMS)
 */
export function getSeoArticleFaqItems(pathname, tierPricing, docuSignAltFaqItems) {
  const p = normalizePathname(pathname)
  switch (p) {
    case '/esignature':
      return ESIGNATURE_HUB_FAQ
    case '/esignature/real-estate':
      return [...ESIGN_RE_EXTRA_FAQ, ...ESIGNATURE_HUB_FAQ]
    case '/esignature/accountants':
      return [...ESIGN_ACCT_EXTRA_FAQ, ...ESIGNATURE_HUB_FAQ]
    case '/esignature/hr':
      return [...ESIGN_HR_EXTRA_FAQ, ...ESIGNATURE_HUB_FAQ]
    case '/esignature/agencies':
      return [...ESIGN_AGENCY_EXTRA_FAQ, ...ESIGNATURE_HUB_FAQ]
    case '/docusign-vs-prime-document-sign':
      return DOCUSIGN_VS_PAGE_FAQ
    case '/docusign-alternative':
      return docuSignAltFaqItems
    case '/free-esignature':
      return freeEsignFaqs(tierPricing)
    case '/templates':
      return TEMPLATES_HUB_FAQ
    case '/templates/nda':
      return templateSubpageFaqWithCore(TPL_NDA_EXTRA_FAQ)
    case '/templates/contract':
      return templateSubpageFaqWithCore(TPL_CONTRACT_EXTRA_FAQ)
    case '/templates/freelance-agreement':
      return templateSubpageFaqWithCore(TPL_FREELANCE_EXTRA_FAQ)
    case '/templates/lease-agreement':
      return templateSubpageFaqWithCore(TPL_LEASE_EXTRA_FAQ)
    case '/templates/service-agreement':
      return templateSubpageFaqWithCore(TPL_SERVICE_EXTRA_FAQ)
    case '/templates/independent-contractor':
      return templateSubpageFaqWithCore(TPL_CONTRACTOR_EXTRA_FAQ)
    case '/templates/purchase-order':
      return templateSubpageFaqWithCore(TPL_PO_EXTRA_FAQ)
    case '/templates/photo-release':
      return templateSubpageFaqWithCore(TPL_PHOTO_EXTRA_FAQ)
    case '/esignature-australia':
      return AU_PAGE_FAQ
    case '/security':
      return SECURITY_TRUST_FAQ
    case '/why-primedocumentsign':
      return WHY_PRIME_FAQ
    case '/about':
      return ABOUT_TRUST_FAQ
    case '/trust':
      return TRUST_MASTER_FAQ
    case '/product':
      return PRODUCT_REFERENCE_FAQ
    case '/alternatives':
      return ALTERNATIVES_HUB_FAQ
    case '/what-is-esignature':
      return WHAT_IS_ESIGN_FAQ
    case '/reviews':
      return REVIEWS_PAGE_FAQ
    case '/how-to-sign-documents-online':
      return HOW_SIGN_ONLINE_FAQ
    case '/free-contract-templates':
      return FREE_CONTRACT_TEMPLATES_FAQ
    case '/esignature-for-freelancers':
      return ESIGN_FREELANCERS_FAQ
    case '/secure-esignature':
      return SECURE_ESIGN_FAQ
    case '/esignature-legality':
      return ESIGNATURE_LEGALITY_PAGE_FAQ
    case '/how-it-works':
      return HOW_IT_WORKS_PAGE_FAQ
    case '/pricing':
      return PRICING_SEO_PAGE_FAQ
    case '/contact':
      return CONTACT_PAGE_FAQ
    case '/terms':
    case '/privacy':
      return []
    default:
      return []
  }
}

export function getMetaTitleKeyForPath(pathname) {
  const p = getCanonicalPathname(pathname)
  const map = {
    '/esignature': 'meta.titleEsignature',
    '/esignature/real-estate': 'meta.titleEsignRealEstate',
    '/esignature/accountants': 'meta.titleEsignAccountants',
    '/esignature/hr': 'meta.titleEsignHr',
    '/esignature/agencies': 'meta.titleEsignAgencies',
    '/docusign-alternative': 'meta.titleDocusignAlt',
    '/docusign-vs-prime-document-sign': 'meta.titleDocuVsPrime',
    '/free-esignature': 'meta.titleFreeEsign',
    '/templates': 'meta.titleTemplates',
    '/templates/nda': 'meta.titleTplNda',
    '/templates/contract': 'meta.titleTplContract',
    '/templates/freelance-agreement': 'meta.titleTplFreelance',
    '/templates/lease-agreement': 'meta.titleTplLease',
    '/templates/service-agreement': 'meta.titleTplService',
    '/templates/independent-contractor': 'meta.titleTplContractor',
    '/templates/purchase-order': 'meta.titleTplPo',
    '/templates/photo-release': 'meta.titleTplPhotoRelease',
    '/esignature-australia': 'meta.titleEsignAu',
    '/why-primedocumentsign': 'meta.titleWhyPrime',
    '/security': 'meta.titleSecurity',
    '/about': 'meta.titleAboutPage',
    '/terms': 'meta.titleTermsOfService',
    '/privacy': 'meta.titlePrivacyPolicy',
    '/pricing': 'meta.titlePricing',
    '/trust': 'meta.titleTrust',
    '/product': 'meta.titleProduct',
    '/alternatives': 'meta.titleAlternatives',
    '/what-is-esignature': 'meta.titleWhatIsEsignature',
    '/reviews': 'meta.titleReviews',
    '/how-to-sign-documents-online': 'meta.titleHowSignOnline',
    '/free-contract-templates': 'meta.titleFreeContractTemplates',
    '/esignature-for-freelancers': 'meta.titleEsignFreelancers',
    '/secure-esignature': 'meta.titleSecureEsign',
    '/esignature-legality': 'meta.titleEsignLegality',
    '/how-it-works': 'meta.titleHowItWorksPage',
    '/contact': 'meta.titleContactPage',
  }
  return map[p] || null
}

export function getMetaDescKeyForPath(pathname) {
  const p = getCanonicalPathname(pathname)
  const map = {
    '/esignature': 'meta.descEsignature',
    '/esignature/real-estate': 'meta.descEsignRealEstate',
    '/esignature/accountants': 'meta.descEsignAccountants',
    '/esignature/hr': 'meta.descEsignHr',
    '/esignature/agencies': 'meta.descEsignAgencies',
    '/docusign-alternative': 'meta.descDocusignAlt',
    '/docusign-vs-prime-document-sign': 'meta.descDocuVsPrime',
    '/free-esignature': 'meta.descFreeEsign',
    '/templates': 'meta.descTemplates',
    '/templates/nda': 'meta.descTplNda',
    '/templates/contract': 'meta.descTplContract',
    '/templates/freelance-agreement': 'meta.descTplFreelance',
    '/templates/lease-agreement': 'meta.descTplLease',
    '/templates/service-agreement': 'meta.descTplService',
    '/templates/independent-contractor': 'meta.descTplContractor',
    '/templates/purchase-order': 'meta.descTplPo',
    '/templates/photo-release': 'meta.descTplPhotoRelease',
    '/esignature-australia': 'meta.descEsignAu',
    '/why-primedocumentsign': 'meta.descWhyPrime',
    '/security': 'meta.descSecurity',
    '/about': 'meta.descAboutPage',
    '/terms': 'meta.descTermsOfService',
    '/privacy': 'meta.descPrivacyPolicy',
    '/pricing': 'meta.descPricing',
    '/trust': 'meta.descTrust',
    '/product': 'meta.descProduct',
    '/alternatives': 'meta.descAlternatives',
    '/what-is-esignature': 'meta.descWhatIsEsignature',
    '/reviews': 'meta.descReviews',
    '/how-to-sign-documents-online': 'meta.descHowSignOnline',
    '/free-contract-templates': 'meta.descFreeContractTemplates',
    '/esignature-for-freelancers': 'meta.descEsignFreelancers',
    '/secure-esignature': 'meta.descSecureEsign',
    '/esignature-legality': 'meta.descEsignLegality',
    '/how-it-works': 'meta.descHowItWorksPage',
    '/contact': 'meta.descContactPage',
  }
  return map[p] || null
}
